import { z } from 'zod';
export const Photo = z.object({
    id: z.string(),
    albumId: z.string(),
    rootAlbumId: z.string(),
    ownerId: z.string(),
    s3Key: z.string(),
    size: z.number().int().nonnegative(),
    encryptedMeta: z.string(),
    uploadStatus: z.enum(['pending', 'complete']),
    // Generation of K_album the blob on S3 is encrypted with. Photos uploaded
    // before key rotation existed default to gen 1 (absent → 1).
    keyGen: z.number().int().positive().optional(),
    // 1-5 stars, encrypted under K_album as a WCV1 container of the digit string
    // ("1".."5"). Absent → not rated. Stored alongside encryptedMeta so a rating
    // change does not require re-encrypting the filename/exif blob.
    encryptedRating: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
});
export const AlbumPhotosResponse = z.object({
    albumId: z.string(),
    photos: z.array(Photo),
});
// Flat listing of every Photo under a root (root + all sub-albums) in a single
// response. Backed by GSI1 ROOT#{rootId}, so a 5k-photo root is one query.
// Used by the duplicate-detection scan, which needs the whole tree before it
// can cluster by size + content hash.
export const RootPhotosTreeResponse = z.object({
    rootAlbumId: z.string(),
    photos: z.array(Photo),
});
// One paginated page of Photos under a root, time-ordered by GSI1SK
// (createdAt + photoId). Used by the cross-album top-rated gallery: the
// client drains pages in the background, decrypts encryptedRating per page,
// and re-sorts by rating in-memory (the server can't sort by an encrypted
// field). `nextCursor` is an opaque base64 of DDB's LastEvaluatedKey;
// absent once the root is fully drained.
export const RootPhotosPageResponse = z.object({
    rootAlbumId: z.string(),
    photos: z.array(Photo),
    nextCursor: z.string().optional(),
});
// `albumId` accompanies `photoId` because Photo rows are keyed on the pair —
// without it the backend would have to scan to resolve the row. Mirrors the
// download batch shape so the UI can reuse the same selection format.
export const DeletePhotosRequest = z.object({
    items: z
        .array(z.object({
        albumId: z.string().min(1),
        photoId: z.string().min(1),
    }))
        .min(1)
        .max(200),
});
export const DeletePhotosResponse = z.object({
    deletedPhotoIds: z.array(z.string()),
    deletedObjects: z.number().int().nonnegative(),
    abortedUploads: z.number().int().nonnegative(),
});
export const DeleteAlbumResponse = z.object({
    deletedIds: z.array(z.string()),
    deletedPhotos: z.number().int().nonnegative(),
    deletedObjects: z.number().int().nonnegative(),
    abortedUploads: z.number().int().nonnegative(),
});
// Bulk move N photos into a single destination album. The handler enforces
// destination.rootAlbumId === photo.rootAlbumId for every item: cross-root
// moves would require re-encryption (different K_album), which is out of
// scope here. The S3 blob is renamed (CopyObject + DeleteObject) since
// s3Key is `${albumId}/${photoId}` and album foglia changes.
export const MovePhotosRequest = z.object({
    items: z
        .array(z.object({
        albumId: z.string().min(1),
        photoId: z.string().min(1),
    }))
        .min(1)
        .max(200),
    destinationAlbumId: z.string().min(1),
});
export const MovePhotosResponse = z.object({
    movedPhotoIds: z.array(z.string()),
    movedObjects: z.number().int().nonnegative(),
});
//# sourceMappingURL=photo.js.map